/**
 * Pronto · Apple In-App Purchases (RevenueCat bridge)
 * ═════════════════════════════════════════════════════════════════════════
 * Wrapper sopra `@revenuecat/purchases-capacitor` per i flussi:
 *   • configure (al login)
 *   • getOfferings → lista prodotti con prezzi App Store localizzati
 *   • purchase(productId)
 *   • restorePurchases()  ← Apple OBBLIGA che ci sia
 *   • showSubscriptionManagement → apre Impostazioni iOS abbonamenti
 *
 * Su web/Android tutte le funzioni sono no-op (ritornano null/false) così
 * il codice chiamante può girare ovunque.
 *
 * iter v30 r145 — Compliance Apple Guideline 3.1.1.
 */
import { isIOS, isNative } from "./native";
import { api } from "./api";
// iter v31 r7 — Import STATICO del plugin RevenueCat (prima era lazy con
// `await import(...)`, ma il chunk dinamico si pianta dentro la WebView iOS
// Capacitor su alcuni dispositivi → `iapConfigure` rimaneva pending finché
// non scattava il timeout. Carichiamo il plugin col main bundle: aggiunge
// ~30KB gzip ma elimina tutto il rischio race-condition / hang.
import { Purchases as _Purchases } from "@revenuecat/purchases-capacitor";

// API key pubblica RevenueCat per iOS — settata via env.
// È pubblica per design (RevenueCat la valida lato server con altri controlli).
const REVENUECAT_API_KEY_IOS = process.env.REACT_APP_REVENUECAT_API_KEY_IOS || "";

let _configured = false;

/** Ritorna il riferimento al plugin Purchases (ora sincrono, no dynamic import). */
function _getPurchases() {
  return _Purchases;
}

/**
 * Configura RevenueCat al login. Idempotente: si auto-skipa se già fatto.
 * @param {string} userId — user_id Pronto (deve combaciare con `app_user_id`
 *                           dei webhook RevenueCat).
 */
export async function iapConfigure(userId) {
  if (!isIOS) return false;
  if (!REVENUECAT_API_KEY_IOS) {
    console.warn("[iap] REACT_APP_REVENUECAT_API_KEY_IOS non configurato");
    return false;
  }
  if (_configured) return true;
  try {
    const Purchases = _getPurchases();
    // iter v31 r6 — Bug fix critico: `Purchases.configure()` su iOS è
    // registrato come `CAPPluginReturnNone` (vedi PurchasesPlugin.swift
    // line 17), quindi la Promise restituita dal bridge JS-nativo NON
    // si risolve MAI. Se la awaitiamo, il paywall resta inchiodato in
    // "Sincronizzazione…" finché non scatta il timeout di 5 s.
    // Fix: chiamiamo senza await (fire-and-forget). Il prossimo
    // `Purchases.getOfferings()` parte subito (RevenueCat SDK ha un
    // ready-queue interno che bufferizza le call finché configure non
    // ha completato in nativo).
    Purchases.configure({
      apiKey: REVENUECAT_API_KEY_IOS,
      appUserID: userId || null,
    });
    _configured = true;
    return true;
  } catch (e) {
    console.error("[iap] configure failed:", e);
    return false;
  }
}

/** Logout: dissocia l'utente da RevenueCat (chiamare al logout Pronto). */
export async function iapLogout() {
  if (!isIOS || !_configured) return;
  try {
    const Purchases = _getPurchases();
    await Purchases.logOut();
  } catch (e) {
    console.warn("[iap] logout warning:", e);
  }
}

/**
 * Fetcha le Offerings RevenueCat (prodotti con prezzi App Store localizzati).
 * @returns {Promise<{ packages: Array<{
 *   identifier: string,
 *   productIdentifier: string,
 *   priceString: string,
 *   price: number,
 *   currencyCode: string,
 *   title: string,
 *   description: string,
 *   subscriptionPeriod: string,  // "P1M" | "P1Y"
 *   raw: object,
 * }> } | null>}
 */
// iter v30 r147 — Cache in memoria delle offerings. La prima chiamata è lenta
// (~3-8s perché RevenueCat → Apple StoreKit), le successive sono istantanee
// finché l'utente non chiude e riapre l'app. Migliora drasticamente la UX
// quando l'utente torna sul paywall (es. dopo "Ripristina acquisti").
//
// iter v31 r8 — BUGFIX valuta: quando lo storefront Apple cambia (o quando
// un primo fetch è avvenuto su storefront US), i prezzi cached restavano in $
// anche se Apple addebitava in €. Ora il paywall passa SEMPRE forceRefresh=true
// al primo mount e in più invalidiamo la cache customerInfo di RevenueCat
// SDK per essere certi che StoreKit interroghi di nuovo Apple per lo storefront
// corrente. La cache JS resta come ottimizzazione SOLO per i mount successivi
// nello stesso ciclo di vita dell'app.
let _offeringsCache = null;

export function iapClearOfferingsCache() {
  _offeringsCache = null;
}

export async function iapGetOfferings({ forceRefresh = false } = {}) {
  if (!isIOS || !_configured) return null;
  if (!forceRefresh && _offeringsCache) return _offeringsCache;
  if (forceRefresh) {
    // Cancella cache locale + chiede a RevenueCat di ricaricare CustomerInfo
    // dal server. RevenueCat SDK non espone un'API pubblica per invalidare la
    // cache offerings, ma rinfrescando CustomerInfo l'SDK aggiorna anche lo
    // storefront associato; la chiamata seguente a getOfferings() ottiene
    // prezzi freschi nella valuta corrente di App Store.
    _offeringsCache = null;
    try {
      const Purchases = _getPurchases();
      if (typeof Purchases.invalidateCustomerInfoCache === "function") {
        await Purchases.invalidateCustomerInfoCache();
      }
    } catch (e) {
      console.warn("[iap] invalidateCustomerInfoCache warning:", e);
    }
  }
  try {
    const Purchases = _getPurchases();
    const res = await Purchases.getOfferings();
    const current = res?.current;
    if (!current) {
      console.warn("[iap] nessuna offering corrente configurata su RevenueCat");
      return { packages: [] };
    }
    const packages = (current.availablePackages || []).map((p) => ({
      identifier: p.identifier,
      productIdentifier: p.product?.identifier,
      priceString: p.product?.priceString,
      price: p.product?.price,
      currencyCode: p.product?.currencyCode,
      title: p.product?.title,
      description: p.product?.description,
      subscriptionPeriod: p.product?.subscriptionPeriod,
      raw: p,
    }));
    const result = { packages };
    if (packages.length > 0) _offeringsCache = result;
    return result;
  } catch (e) {
    console.error("[iap] getOfferings failed:", e);
    return null;
  }
}

/**
 * Avvia l'acquisto di un package RevenueCat (apre il foglio nativo Apple).
 * @param {object} pkg — package object ottenuto da iapGetOfferings()
 * @returns {Promise<{ ok: boolean, cancelled?: boolean, entitlement?: object, error?: string }>}
 */
export async function iapPurchase(pkg) {
  if (!isIOS) return { ok: false, error: "Disponibile solo su iPhone" };
  if (!_configured) return { ok: false, error: "RevenueCat non configurato" };
  if (!pkg?.raw) return { ok: false, error: "Pacchetto non valido" };
  try {
    const Purchases = _getPurchases();
    const res = await Purchases.purchasePackage({ aPackage: pkg.raw });
    const entitlements = res?.customerInfo?.entitlements?.active || {};
    const proEntitlement = Object.values(entitlements)[0] || null;
    return {
      ok: true,
      entitlement: proEntitlement,
      customerInfo: res?.customerInfo,
    };
  } catch (e) {
    // userCancelled è una property della error di RevenueCat
    if (e?.userCancelled || e?.code === "PURCHASE_CANCELLED" || /cancel/i.test(String(e?.message || ""))) {
      return { ok: false, cancelled: true };
    }
    console.error("[iap] purchase failed:", e);
    return { ok: false, error: e?.message || "Acquisto non riuscito" };
  }
}

/**
 * Ripristina gli acquisti precedenti (OBBLIGATORIO per Apple Review).
 * @returns {Promise<{ ok: boolean, hasActive: boolean, entitlement?: object, error?: string }>}
 */
export async function iapRestorePurchases() {
  if (!isIOS) return { ok: false, error: "Disponibile solo su iPhone" };
  if (!_configured) return { ok: false, error: "RevenueCat non configurato" };
  try {
    const Purchases = _getPurchases();
    const res = await Purchases.restorePurchases();
    const entitlements = res?.customerInfo?.entitlements?.active || {};
    const active = Object.values(entitlements)[0] || null;
    return {
      ok: true,
      hasActive: !!active,
      entitlement: active,
      customerInfo: res?.customerInfo,
    };
  } catch (e) {
    console.error("[iap] restore failed:", e);
    return { ok: false, hasActive: false, error: e?.message || "Ripristino non riuscito" };
  }
}

/** Apre la schermata "Gestisci abbonamento" delle Impostazioni iOS.
 *  iter v31 r6 — `Purchases.showManageSubscriptions()` NON esiste in
 *  @revenuecat/purchases-capacitor v11. Usiamo invece il deep link Apple
 *  ufficiale `itms-apps://apps.apple.com/account/subscriptions` che apre
 *  direttamente la pagina Abbonamenti dentro l'app Impostazioni iOS. */
export async function iapShowManageSubscription() {
  if (!isIOS) return false;
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url: "itms-apps://apps.apple.com/account/subscriptions" });
    return true;
  } catch (e) {
    console.warn("[iap] open manage subscriptions failed:", e);
    try {
      // Fallback: window.location (su iOS Capacitor di solito non funziona
      // per scheme nativi, ma proviamo comunque)
      window.location.href = "itms-apps://apps.apple.com/account/subscriptions";
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Estrae da un entitlement RevenueCat i dati che servono al /sync backend.
 * @param {object} entitlement — entitlement object ritornato da purchase/restore
 * @returns {object|null} payload pronto per POST /api/iap/sync
 */
export function iapBuildSyncPayload(entitlement) {
  if (!entitlement) return null;
  return {
    product_id: entitlement.productIdentifier || entitlement.product_identifier,
    original_transaction_id:
      entitlement.originalPurchaseDate ||
      entitlement.originalTransactionIdentifier ||
      entitlement.original_purchase_date ||
      "",
    transaction_id: entitlement.latestPurchaseDate || entitlement.latest_purchase_date || "",
    expires_at: entitlement.expirationDate || entitlement.expiration_date || null,
    is_trial: !!(entitlement.periodType === "TRIAL" || entitlement.period_type === "trial"),
    is_active: !!(entitlement.isActive ?? entitlement.is_active ?? true),
    raw_entitlement: entitlement,
  };
}

/** Notifica il backend dell'acquisto/restore appena completato. */
export async function iapSyncToBackend(entitlement) {
  const payload = iapBuildSyncPayload(entitlement);
  if (!payload || !payload.product_id) return null;
  try {
    const { data } = await api.post("/iap/sync", payload);
    return data;
  } catch (e) {
    console.error("[iap] sync to backend failed:", e);
    return null;
  }
}

/** Esporta utility per check rapido. */
export const iapAvailable = isIOS && isNative;
